import { createPublicClient, http, WalletClient } from 'viem';
import { CHAIN_CONFIGS, getWHChainIdByEVMChainId } from './providers';
import { ALGO_NETWORK } from '../constants/constants';
import { isCancelError } from '../_utils/errorHandlers';
import { wagmiConfig } from './evm/evm.service';
import { getWalletClient } from '@wagmi/core';

export const madePRCRequest = async (evmChainId: number, action: (fc: any, rpc?: string , rpsIndex?: number) => Promise<any>, rpsIndex = 0) => {
  const { publicClient, rpc, rpsIndex: currentRPCIndex } = getPublicClient(evmChainId, rpsIndex);
  try {
    return await action(publicClient, rpc, currentRPCIndex);
  } catch (err) {
    if (isCancelError(err?.message)) {
      throw err;
    }
    const whChainId = getWHChainIdByEVMChainId(evmChainId);
    if ((HTTPSRPCs[whChainId].length - 1) > rpsIndex) {
      const newRPCIndex = rpsIndex + 1;
      return await madePRCRequest(evmChainId, action, newRPCIndex);
    } else {
      throw err;
    }
  }
}

export const getPublicClient = (evmChainId: number, rpsIndex: number): { publicClient: any, rpc: string, rpsIndex: number } => {
  const whChainId = getWHChainIdByEVMChainId(evmChainId);
  const rpc = HTTPSRPCs[whChainId][rpsIndex];

  const publicClient = createPublicClient({
    chain: CHAIN_CONFIGS.get(evmChainId),
    transport: http(rpc),
  });

  return { publicClient, rpc, rpsIndex };
}

export const getCorrectWalletClient = async (evmChainId: number, rpsIndex: number): Promise<WalletClient> => {
  const whChainId = getWHChainIdByEVMChainId(evmChainId);
  const walletClient = await getWalletClient(wagmiConfig);
  const rpc = HTTPSRPCs[whChainId][rpsIndex] ? HTTPSRPCs[whChainId][rpsIndex] : walletClient.chain.rpcUrls.default.http[0];

  const newWalletClient = {...walletClient, chain: {...walletClient.chain, rpcUrls: { default: { http: [ rpc ] }}}}
  return newWalletClient;
}

const HTTPSRPC_TESTNET = {
  4: [
    '',
    'https://bsc-testnet.public.blastapi.io',
    'https://bsc-testnet.blockpi.network/v1/rpc/private',
    'https://bsc-testnet-rpc.publicnode.com',
    'https://api.zan.top/bsc-testnet',
    'https://data-seed-prebsc-2-s1.bnbchain.org:8545',
    'https://data-seed-prebsc-2-s2.bnbchain.org:8545',
    'https://data-seed-prebsc-2-s3.bnbchain.org:8545',
    'https://data-seed-prebsc-1-s1.bnbchain.org:8545',
    'https://data-seed-prebsc-1-s2.bnbchain.org:8545',
    'https://data-seed-prebsc-1-s3.bnbchain.org:8545',
    'https://endpoints.omniatech.io/v1/bsc/testnet/public',
  ],
  6: [
    '',
    'https://rpc.ankr.com/avalanche_fuji',
    'https://rpc.ankr.com/avalanche_fuji-c',
    'https://api.avax-test.network/ext/bc/C/rpc',
    'https://ava-testnet.public.blastapi.io/ext/bc/C/rpc',
    'https://endpoints.omniatech.io/v1/avax/fuji/public',
    'https://avalanche-fuji.blockpi.network/v1/rpc/private',
    'https://avalanche-fuji-c-chain-rpc.publicnode.com',
    'https://avalanche-fuji.blockpi.network/v1/rpc/private',
  ],
  10002: [
    'https://ethereum-sepolia-rpc.publicnode.com',
    'https://sepolia.gateway.tenderly.co',
    'https://eth-sepolia.public.blastapi.io',
    'https://ethereum-sepolia.blockpi.network/v1/rpc/private',
    'https://sepolia.drpc.org',
    'https://endpoints.omniatech.io/v1/eth/sepolia/public',
    'https://1rpc.io/sepolia',
    'https://gateway.tenderly.co/public/sepolia',
    'https://sepolia.gateway.tenderly.co',
    'https://api.zan.top/eth-sepolia',
    'https://ethereum-sepolia.rpc.subquery.network/public',
  ],
  10003: [
    '',
    'https://arbitrum-sepolia.blockpi.network/v1/rpc/private ',
    'https://arbitrum-sepolia.gateway.tenderly.co',
    'https://endpoints.omniatech.io/v1/arbitrum/sepolia/public',
    'https://api.zan.top/arb-sepolia',
    'https://sepolia-rollup.arbitrum.io/rpc',
  ],
  10004: [
    '',
    'https://sepolia.base.org',
    'https://base-sepolia-rpc.publicnode.com',
    'https://base-sepolia.gateway.tenderly.co',
    'https://base-sepolia.blockpi.network/v1/rpc/private',
    'https://node.histori.xyz/base-sepolia/8ry9f6t9dct1se2hlagxnd9n2a',
  ],
  10005: [
    '',
    'https://optimism-sepolia.blockpi.network/v1/rpc/private',
    'https://sepolia.optimism.io',
    'https://api.zan.top/opt-sepolia',
    'https://optimism-sepolia.gateway.tenderly.co',
    'https://optimism-sepolia.drpc.org',
    'https://endpoints.omniatech.io/v1/op/sepolia/public',
  ],
  10007: [
    'https://rpc-amoy.polygon.technology',
    'https://polygon-amoy-bor-rpc.publicnode.com',
    'https://rpc.ankr.com/polygon_amoy',
    'https://polygon-amoy.drpc.org',
    'https://api.zan.top/polygon-amoy',
    'https://polygon-amoy.gateway.tenderly.co',
  ],
}

const HTTPSRPC_MAINNET = {
  2: [
    '',
    'https://eth.llamarpc.com',
    'https://ethereum.blockpi.network/v1/rpc/public',
    'https://eth-mainnet.nodereal.io/v1/1659dfb40aa24bbb8153a677b98064d7',
    'https://uk.rpc.blxrbdn.com',
    'https://singapore.rpc.blxrbdn.com',
    'https://virginia.rpc.blxrbdn.com',
    'https://eth-mainnet.public.blastapi.io',
    'https://api.securerpc.com/v1',
    'https://mainnet.gateway.tenderly.co',
    'https://eth-pokt.nodies.app',
    'https://eth.blockrazor.xyz',
    'https://eth.merkle.io',
    'https://gateway.tenderly.co/public/mainnet',
    'https://eth.rpc.blxrbdn.com',
  ],
  4: [
    '',
    'https://bsc.blockrazor.xyz',
    'https://bscrpc.com',
    'https://bsc-mainnet.public.blastapi.io',
    'https://binance.llamarpc.com',
    'https://go.getblock.io/cc778cdbdf5c4b028ec9456e0e6c0cf3',
    'https://bsc-pokt.nodies.app',
    'https://56.rpc.thirdweb.com',
    'https://bsc.blockpi.network/v1/rpc/private',
    'https://bsc.rpc.blxrbdn.com',
    'https://bsc-dataseed.bnbchain.org',
    'https://endpoints.omniatech.io/v1/bsc/mainnet/public',
    'https://bsc-dataseed1.ninicoin.io',
    'https://bsc-dataseed2.defibit.io',
    'https://bsc-dataseed2.ninicoin.io',
    'https://0.48.club',
    'https://bsc-dataseed3.defibit.io',
  ],
  5: [
    '',
    'https://polygon.llamarpc.com',
    'https://polygon-rpc.com',
    'https://rpc-mainnet.matic.quiknode.pro',
    'https://endpoints.omniatech.io/v1/matic/mainnet/public',
    'https://polygon-pokt.nodies.app',
    'https://polygon-bor-rpc.publicnode.com',
    'https://polygon.rpc.subquery.network/public',
    'https://polygon-bor-rpc.publicnode.com',
    'https://polygon.blockpi.network/v1/rpc/private',
    'https://polygon.api.onfinality.io/public',
    'https://polygon.lava.build',
    'https://api.zan.top/polygon-mainnet',
    'https://polygon.meowrpc.com',
  ],
  6: [
    '',
    'https://avalanche-c-chain-rpc.publicnode.com',
    'https://ava-mainnet.public.blastapi.io/ext/bc/C/rpc',
    'https://endpoints.omniatech.io/v1/avax/mainnet/public',
    'https://avalanche-c-chain-rpc.publicnode.com',
    'https://avalanche.blockpi.network/v1/rpc/private',
    'https://avalanche.public-rpc.com',
    'https://avalanche.drpc.org',
    'https://avax.meowrpc.com',
    'https://endpoints.omniatech.io/v1/avax/mainnet/public',
    'https://api.avax.network/ext/bc/C/rpc',
    'https://1rpc.io/avax/c',
    'https://avax-pokt.nodies.app/ext/bc/C/rpc',
    'https://endpoints.omniatech.io/v1/avax/mainnet/public',
    'https://api.zan.top/avax-mainnet/ext/bc/C/rpc',
  ],
  23: [
    '',
    'https://arbitrum.llamarpc.com',
    'https://arbitrum.rpc.subquery.network/public',
    'https://endpoints.omniatech.io/v1/arbitrum/one/public',
    'https://arbitrum.meowrpc.com',
    'https://arbitrum.gateway.tenderly.co',
    'https://arbitrum-one.public.blastapi.io',
    'https://arb1.lava.build',
    'https://arbitrum-one.publicnode.com',
    'https://arbitrum.blockpi.network/v1/rpc/public',
    'https://arb-pokt.nodies.app',
    'https://api.zan.top/arb-one',
    'https://arb1.arbitrum.io/rpc',
    'https://arbitrum.drpc.org',
    'https://1rpc.io/arb',
  ],
  24: [
    '',
    'https://optimism.llamarpc.com',
    'https://op-pokt.nodies.app',
    'https://optimism-mainnet.public.blastapi.io',
    'https://optimism.blockpi.network/v1/rpc/public',
    'https://endpoints.omniatech.io/v1/op/mainnet/public',
    'https://optimism-rpc.publicnode.com',
    'https://optimism.drpc.org',
    'https://1rpc.io/op',
    'https://optimism.gateway.tenderly.co',
    'https://optimism.lava.build',
    'https://optimism.rpc.subquery.network/public',
    'https://mainnet.optimism.io',
    'https://optimism-rpc.publicnode.com',
    'https://gateway.tenderly.co/public/optimism',
  ],
  30: [
    '',
    'https://base.llamarpc.com',
    'https://base-mainnet.public.blastapi.io',
    'https://base-rpc.publicnode.com',
    'https://base.blockpi.network/v1/rpc/public',
    'https://base.drpc.org',
    'https://mainnet.base.org',
    'https://base.api.onfinality.io/public',
    'https://base-pokt.nodies.app',
    'https://1rpc.io/base',
    'https://developer-access-mainnet.base.org',
    'https://endpoints.omniatech.io/v1/base/mainnet/public',
    'https://base.rpc.subquery.network/public',
    'https://base.api.onfinality.io/public',
    'https://base.meowrpc.com',
    'https://base.gateway.tenderly.co',
  ],
}

export const HTTPSRPCs = ALGO_NETWORK === 'MainNet' ? HTTPSRPC_MAINNET : HTTPSRPC_TESTNET;
